import Head from "next/head";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSettings } from "@/redux/settings";
import { useUser } from "@/redux/user";
import { api } from "@/services/api";
import { Button } from "@/components/ui/button";
import { LEAGUE_NAME_PLACEMENT } from "@chesshotel/constants";
import PuzzleBoard, { PuzzleRatingUpdate } from "./PuzzleBoard";
import {
  createEmptyPuzzleSolvedProgress,
  getPuzzleLeagueMedalImageSrc,
  getPuzzleLeagueTier,
  getPuzzleSolvedProgressStorageKey,
  PUZZLE_PLACEMENT_TARGET,
  PuzzleLeagueTier,
  readPuzzleSolvedProgress,
  recordPuzzleSolved,
} from "./puzzleMedals";
import {
  getPuzzleDifficultyOption,
  PuzzleDifficultyKey,
  readPuzzleDifficulty,
  writePuzzleDifficulty,
} from "./puzzleDifficulty";

const MAX_PLAYED_PUZZLES = 300;
const STARTING_PUZZLE_RATING = 800;
const PUZZLE_PLACEMENT_SOLVES = PUZZLE_PLACEMENT_TARGET;
const PUZZLE_PLACEMENT_K_FACTOR = 96;
const PUZZLE_STANDARD_K_FACTOR = 32;
const GUEST_PUZZLE_RATING_STORAGE_KEY = "puzzle_rating_guest_v2";
const GUEST_PUZZLE_ATTEMPTS_STORAGE_KEY = "puzzle_rating_attempts_guest_v2";
const GUEST_PUZZLE_POINTS_STORAGE_KEY = "puzzle_points_guest_v1";

export type PuzzlePayload = {
  puzzleId?: string;
  fen: string;
  moves: string;
  rating?: number;
  userElo?: number;
  gameNr?: number;
  leagueName?: string;
  divisionId?: number;
  solvedCount?: number;
  points?: number;
  cleanSolveStreak?: number;
  rank?: number | null;
  nextRank?: number | null;
  nextRankPoints?: number | null;
};

function getPlayedPuzzleStorageKey(username?: string) {
  return username ? `played_puzzles_${username}` : "played_puzzles_guest";
}

function readPlayedPuzzleIds(storageKey: string) {
  try {
    const storedPlayed = localStorage.getItem(storageKey);
    const parsed = storedPlayed ? JSON.parse(storedPlayed) : [];
    return Array.isArray(parsed)
      ? parsed.filter((id): id is string => typeof id === "string")
      : [];
  } catch (e) {
    console.error("Error reading played puzzles", e);
    return [];
  }
}

function rememberPlayedPuzzle(storageKey: string, puzzleId: string) {
  try {
    const played = readPlayedPuzzleIds(storageKey);
    if (!played.includes(puzzleId)) {
      played.push(puzzleId);
    }
    const recentPlayed = played.slice(-MAX_PLAYED_PUZZLES);
    localStorage.setItem(storageKey, JSON.stringify(recentPlayed));
    return recentPlayed;
  } catch (e) {
    console.error("Error saving played puzzle", e);
    return [];
  }
}

function normalizePuzzleRating(value: unknown) {
  const rating = Number(value);
  return Number.isFinite(rating) && rating > 0
    ? Math.round(rating)
    : STARTING_PUZZLE_RATING;
}

function normalizePuzzleAttempts(value: unknown) {
  const attempts = Number(value);
  return Number.isFinite(attempts) && attempts >= 0
    ? Math.floor(attempts)
    : 0;
}

function readGuestPuzzleRating() {
  if (typeof window === "undefined") return STARTING_PUZZLE_RATING;

  try {
    return normalizePuzzleRating(
      window.localStorage.getItem(GUEST_PUZZLE_RATING_STORAGE_KEY),
    );
  } catch (e) {
    console.error("Error reading puzzle rating", e);
    return STARTING_PUZZLE_RATING;
  }
}

function writeGuestPuzzleRating(rating: number) {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(
      GUEST_PUZZLE_RATING_STORAGE_KEY,
      String(normalizePuzzleRating(rating)),
    );
  } catch (e) {
    console.error("Error saving puzzle rating", e);
  }
}

function readGuestPuzzleAttempts() {
  if (typeof window === "undefined") return 0;

  try {
    return normalizePuzzleAttempts(
      window.localStorage.getItem(GUEST_PUZZLE_ATTEMPTS_STORAGE_KEY),
    );
  } catch (e) {
    console.error("Error reading puzzle attempts", e);
    return 0;
  }
}

function writeGuestPuzzleAttempts(attempts: number) {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(
      GUEST_PUZZLE_ATTEMPTS_STORAGE_KEY,
      String(normalizePuzzleAttempts(attempts)),
    );
  } catch (e) {
    console.error("Error saving puzzle attempts", e);
  }
}

function readGuestPuzzlePoints() {
  if (typeof window === "undefined") return 0;

  try {
    return normalizePuzzleAttempts(
      window.localStorage.getItem(GUEST_PUZZLE_POINTS_STORAGE_KEY),
    );
  } catch (e) {
    console.error("Error reading puzzle points", e);
    return 0;
  }
}

function writeGuestPuzzlePoints(points: number) {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(
      GUEST_PUZZLE_POINTS_STORAGE_KEY,
      String(normalizePuzzleAttempts(points)),
    );
  } catch (e) {
    console.error("Error saving puzzle points", e);
  }
}

function isPuzzleRatingRevealed({
  solved,
}: {
  solved: number;
}) {
  return solved >= PUZZLE_PLACEMENT_SOLVES;
}

function PuzzleChallengeMessage({
  title,
  text,
}: {
  title: string;
  text: string;
}) {
  const settings = useSettings();
  const boardSize = settings.boardSize || 504;
  const panelHeight = settings.isDesktop ? boardSize + 44 + "px" : undefined;

  return (
    <div className="flex flex-col items-center md:mt-4">
      <div className="flex w-full flex-col items-center md:w-auto md:flex-row md:items-start md:rounded-lg">
        <div className="flex justify-center md:mr-4">
          <div
            className="flex items-center justify-center rounded-md border border-athens-gray-150 bg-white text-sm text-gray-500"
            style={{ width: boardSize, height: boardSize }}
          >
            {title}
          </div>
        </div>
        <div
          className="mb-20 flex h-full w-full flex-col justify-center rounded-md bg-athens-gray-150 px-5 text-sm md:mb-0 md:w-[400px] lg:w-[400px]"
          style={{ height: panelHeight }}
        >
          <div className="text-lg font-bold text-gray-800">{title}</div>
          <div className="mt-2 text-muted-foreground">{text}</div>
        </div>
      </div>
    </div>
  );
}

function PuzzleLeagueUnlockedPopup({
  tier,
  onClose,
}: {
  tier: PuzzleLeagueTier;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-gray-950/45" onClick={onClose} />
      <div className="relative flex w-full max-w-[420px] flex-col items-center rounded-lg bg-white px-6 pb-6 pt-7 text-center shadow-2xl">
        <button
          className="absolute right-4 top-3 text-2xl leading-none text-gray-400 hover:text-gray-700"
          onClick={onClose}
          type="button"
        >
          ×
        </button>
        <img
          src={getPuzzleLeagueMedalImageSrc(tier)}
          alt={`${tier.label} puzzle league`}
          className="h-24 w-24 object-contain drop-shadow-md"
        />
        <div className="mt-4 text-xl font-bold text-gray-900">
          Puzzle league unlocked
        </div>
        <div className="mt-2 text-sm text-muted-foreground">
          You entered the {tier.label.toLowerCase()} puzzle league.
        </div>
        <Button className="mt-6 w-full" size="lg" onClick={onClose}>
          Continue solving
        </Button>
      </div>
    </div>
  );
}

type PuzzleChallengeProps = {
  initialPuzzle?: PuzzlePayload | null;
};

export default function PuzzleChallenge({
  initialPuzzle = null,
}: PuzzleChallengeProps) {
  const user = useUser();
  const username = user?.username;
  const [currentPuzzle, setCurrentPuzzle] =
    useState<PuzzlePayload | null>(initialPuzzle);
  const [prefetchedPuzzle, setPrefetchedPuzzle] =
    useState<PuzzlePayload | null>(null);
  const [nextLoading, setNextLoading] = useState(false);
  const [solvedProgress, setSolvedProgress] = useState(
    createEmptyPuzzleSolvedProgress,
  );
  const [cleanSolveStreak, setCleanSolveStreak] = useState(0);
  const [unlockedLeague, setUnlockedLeague] =
    useState<PuzzleLeagueTier | null>(null);
  const [puzzleDifficulty, setPuzzleDifficulty] =
    useState<PuzzleDifficultyKey>("balanced");
  const [puzzleRating, setPuzzleRating] = useState(
    normalizePuzzleRating(initialPuzzle?.userElo),
  );
  const [ratingGameNr, setRatingGameNr] = useState(
    normalizePuzzleAttempts(initialPuzzle?.gameNr),
  );
  const [ratingLeagueName, setRatingLeagueName] = useState<string | undefined>(
    initialPuzzle?.leagueName,
  );
  const [puzzlePoints, setPuzzlePoints] = useState(
    normalizePuzzleAttempts(initialPuzzle?.points),
  );
  const [lastPointsEarned, setLastPointsEarned] = useState<number | null>(null);
  const [lastStreakBonus, setLastStreakBonus] = useState(0);
  const [rank, setRank] = useState<number | null>(initialPuzzle?.rank ?? null);
  const [nextRank, setNextRank] = useState<number | null | undefined>(
    initialPuzzle?.nextRank ?? null,
  );
  const [nextRankPoints, setNextRankPoints] = useState<
    number | null | undefined
  >(
    initialPuzzle?.nextRankPoints ?? null,
  );
  const [guestPuzzleAttempts, setGuestPuzzleAttempts] = useState(0);
  const prefetchingRef = useRef(false);
  const currentPuzzleIdRef = useRef<string | undefined>(
    initialPuzzle?.puzzleId,
  );
  const puzzleDifficultyRef = useRef<PuzzleDifficultyKey>("balanced");
  const puzzleRatingRef = useRef(STARTING_PUZZLE_RATING);
  const usernameRef = useRef<string | undefined>(undefined);
  const syncedUserPuzzleRef = useRef<string | null>(null);

  useEffect(() => {
    currentPuzzleIdRef.current = currentPuzzle?.puzzleId;
  }, [currentPuzzle?.puzzleId]);

  useEffect(() => {
    puzzleDifficultyRef.current = puzzleDifficulty;
  }, [puzzleDifficulty]);

  useEffect(() => {
    puzzleRatingRef.current = puzzleRating;
  }, [puzzleRating]);

  const applyFetchedPuzzleRating = useCallback((puzzle: PuzzlePayload) => {
    if (puzzle.userElo !== undefined) {
      setPuzzleRating(normalizePuzzleRating(puzzle.userElo));
    }
    if (puzzle.gameNr !== undefined) {
      setRatingGameNr(normalizePuzzleAttempts(puzzle.gameNr));
    }
    if (puzzle.leagueName !== undefined) {
      setRatingLeagueName(puzzle.leagueName);
    }
    if (puzzle.solvedCount !== undefined) {
      const solvedCount = normalizePuzzleAttempts(puzzle.solvedCount);
      setSolvedProgress((currentProgress) => ({
        ...currentProgress,
        solved: Math.max(currentProgress.solved, solvedCount),
      }));
    }
    if (puzzle.points !== undefined) {
      setPuzzlePoints(normalizePuzzleAttempts(puzzle.points));
    }
    if (puzzle.cleanSolveStreak !== undefined) {
      setCleanSolveStreak(normalizePuzzleAttempts(puzzle.cleanSolveStreak));
    }
    if (puzzle.rank !== undefined) {
      setRank(puzzle.rank);
    }
    if (puzzle.nextRank !== undefined) {
      setNextRank(puzzle.nextRank);
    }
    if (puzzle.nextRankPoints !== undefined) {
      setNextRankPoints(puzzle.nextRankPoints);
    }
  }, []);

  const requestPuzzle = useCallback(
    async (extraPlayedPuzzleIds: string[] = []) => {
      const difficultyOption = getPuzzleDifficultyOption(puzzleDifficulty);
      const storageKey = getPlayedPuzzleStorageKey(username);
      const playedPuzzleIds = Array.from(
        new Set([
          ...readPlayedPuzzleIds(storageKey),
          ...extraPlayedPuzzleIds.filter(Boolean),
        ]),
      ).slice(-MAX_PLAYED_PUZZLES);

      const response = await api.post("/newpuzzle/random", {
        elo: puzzleRating,
        range: difficultyOption.range,
        difficultyOffset: difficultyOption.ratingOffset,
        playedPuzzleIds,
      });

      return response.data as PuzzlePayload;
    },
    [puzzleDifficulty, puzzleRating, username],
  );

  const requestPuzzleFromRefs = useCallback(
    async (extraPlayedPuzzleIds: string[] = []) => {
      const currentUsername = usernameRef.current;
      const difficultyOption = getPuzzleDifficultyOption(
        puzzleDifficultyRef.current,
      );
      const storageKey = getPlayedPuzzleStorageKey(currentUsername);
      const playedPuzzleIds = Array.from(
        new Set([
          ...readPlayedPuzzleIds(storageKey),
          ...extraPlayedPuzzleIds.filter(Boolean),
        ]),
      ).slice(-MAX_PLAYED_PUZZLES);

      const response = await api.post("/newpuzzle/random", {
        elo: puzzleRatingRef.current,
        range: difficultyOption.range,
        difficultyOffset: difficultyOption.ratingOffset,
        playedPuzzleIds,
      });

      return response.data as PuzzlePayload;
    },
    [],
  );

  const prefetchPuzzle = useCallback(
    async (extraPlayedPuzzleIds: string[] = []) => {
      if (prefetchingRef.current) return;

      const requestedDifficulty = puzzleDifficulty;
      const requestedRating = puzzleRating;
      prefetchingRef.current = true;
      try {
        const puzzle = await requestPuzzle(extraPlayedPuzzleIds);
        if (
          puzzleDifficultyRef.current === requestedDifficulty &&
          puzzleRatingRef.current === requestedRating
        ) {
          setPrefetchedPuzzle(puzzle);
        }
      } catch (e) {
        console.error("Error prefetching puzzle", e);
      } finally {
        prefetchingRef.current = false;
      }
    },
    [puzzleDifficulty, puzzleRating, requestPuzzle],
  );

  useEffect(() => {
    const previousUsername = usernameRef.current;
    usernameRef.current = username;
    setPrefetchedPuzzle(null);
    setSolvedProgress(
      readPuzzleSolvedProgress(getPuzzleSolvedProgressStorageKey(username)),
    );

    if (username) {
      if (previousUsername !== username) {
        setPuzzleRating(STARTING_PUZZLE_RATING);
        setRatingGameNr(0);
        setRatingLeagueName(LEAGUE_NAME_PLACEMENT);
        setPuzzlePoints(0);
        setLastPointsEarned(null);
        setLastStreakBonus(0);
        setCleanSolveStreak(0);
        setRank(null);
        setNextRank(null);
        setNextRankPoints(null);
      }

      if (syncedUserPuzzleRef.current !== username) {
        syncedUserPuzzleRef.current = username;
        const requestedUsername = username;
        const currentPuzzleId = currentPuzzleIdRef.current;

        void requestPuzzleFromRefs(
          currentPuzzleId ? [currentPuzzleId] : [],
        )
          .then((puzzle) => {
            if (usernameRef.current !== requestedUsername) return;
            applyFetchedPuzzleRating(puzzle);
            setPrefetchedPuzzle(puzzle);
          })
          .catch((e) => {
            if (usernameRef.current !== requestedUsername) return;
            console.error("Error syncing puzzle rating", e);
          });
      }
      return;
    }

    syncedUserPuzzleRef.current = null;
    const storedGuestRating = readGuestPuzzleRating();
    const storedGuestAttempts = readGuestPuzzleAttempts();
    const storedGuestPoints = readGuestPuzzlePoints();
    setPuzzleRating(storedGuestRating);
    setGuestPuzzleAttempts(storedGuestAttempts);
    setRatingGameNr(storedGuestAttempts);
    setRatingLeagueName(undefined);
    setPuzzlePoints(storedGuestPoints);
    setRank(null);
    setNextRank(undefined);
    setNextRankPoints(undefined);
  }, [applyFetchedPuzzleRating, requestPuzzleFromRefs, username]);

  useEffect(() => {
    setPuzzleDifficulty(readPuzzleDifficulty());
  }, []);

  useEffect(() => {
    setPrefetchedPuzzle(null);
    if (currentPuzzle?.puzzleId) {
      void prefetchPuzzle([currentPuzzle.puzzleId]);
    }
  }, [currentPuzzle?.puzzleId, prefetchPuzzle, puzzleDifficulty]);

  useEffect(() => {
    let cancelled = false;

    async function preparePuzzle() {
      if (currentPuzzle) {
        if (!prefetchedPuzzle && currentPuzzle.puzzleId) {
          void prefetchPuzzle([currentPuzzle.puzzleId]);
        }
        return;
      }

      try {
        const puzzle = await requestPuzzle();
        if (cancelled) return;
        applyFetchedPuzzleRating(puzzle);
        setCurrentPuzzle(puzzle);
        if (puzzle.puzzleId) {
          void prefetchPuzzle([puzzle.puzzleId]);
        }
      } catch (e) {
        if (cancelled) return;
        console.error("Error fetching puzzle", e);
        setCurrentPuzzle(null);
      }
    }

    void preparePuzzle();

    return () => {
      cancelled = true;
    };
  }, [currentPuzzle, prefetchedPuzzle, prefetchPuzzle, requestPuzzle]);

  const handleNext = async () => {
    if (nextLoading) return;

    const currentPuzzleId = currentPuzzle?.puzzleId;

    if (prefetchedPuzzle) {
      const nextPuzzle = prefetchedPuzzle;
      if (username) {
        applyFetchedPuzzleRating(nextPuzzle);
      }
      setCurrentPuzzle(nextPuzzle);
      setPrefetchedPuzzle(null);
      if (nextPuzzle.puzzleId) {
        void prefetchPuzzle([nextPuzzle.puzzleId]);
      }
      return;
    }

    setNextLoading(true);
    try {
      const puzzle = await requestPuzzle(
        currentPuzzleId ? [currentPuzzleId] : [],
      );
      applyFetchedPuzzleRating(puzzle);
      setCurrentPuzzle(puzzle);
      if (puzzle.puzzleId) {
        void prefetchPuzzle([puzzle.puzzleId]);
      }
    } catch (e) {
      console.error("Error fetching puzzle", e);
    } finally {
      setNextLoading(false);
    }
  };

  const handleComplete = () => {
    if (!currentPuzzle?.puzzleId) return;

    const storageKey = getPlayedPuzzleStorageKey(username);
    rememberPlayedPuzzle(storageKey, currentPuzzle.puzzleId);
    const nextSolvedProgress = recordPuzzleSolved(
      getPuzzleSolvedProgressStorageKey(username),
      currentPuzzle.puzzleId,
    );
    setSolvedProgress(nextSolvedProgress);

    if (!prefetchedPuzzle) {
      void prefetchPuzzle([currentPuzzle.puzzleId]);
    }
  };

  const handleResult = (won: boolean, cleanSolve: boolean) => {
    if (!won || !cleanSolve) {
      setCleanSolveStreak(0);
      setLastStreakBonus(0);
    }
  };

  const handleDifficultyChange = (difficulty: PuzzleDifficultyKey) => {
    setPuzzleDifficulty(difficulty);
    writePuzzleDifficulty(difficulty);
    setPrefetchedPuzzle(null);
  };

  const ratingRevealed = isPuzzleRatingRevealed({
    solved: solvedProgress.solved,
  });

  const handleRatingChange = (ratingUpdate: PuzzleRatingUpdate) => {
    const nextRating = normalizePuzzleRating(ratingUpdate.newElo);
    let nextGameNr =
      ratingUpdate.gameNr !== undefined
        ? normalizePuzzleAttempts(ratingUpdate.gameNr)
        : ratingGameNr;
    let nextLeagueName = ratingUpdate.leagueName ?? ratingLeagueName;
    const puzzleAlreadySolved = currentPuzzle?.puzzleId
      ? solvedProgress.completedPuzzleIds.includes(currentPuzzle.puzzleId)
      : false;
    const predictedSolvedCount = ratingUpdate.won && !puzzleAlreadySolved
      ? solvedProgress.solved + 1
      : solvedProgress.solved;
    const nextSolvedCount =
      ratingUpdate.solvedCount !== undefined
        ? Math.max(
            predictedSolvedCount,
            normalizePuzzleAttempts(ratingUpdate.solvedCount),
          )
        : predictedSolvedCount;

    setPuzzleRating(nextRating);
    setPrefetchedPuzzle(null);

    if (!username) {
      nextGameNr = guestPuzzleAttempts + 1;
      nextLeagueName =
        nextSolvedCount >= PUZZLE_PLACEMENT_SOLVES
          ? getPuzzleLeagueTier({ rating: nextRating }).leagueName
          : undefined;
      writeGuestPuzzleRating(nextRating);
      writeGuestPuzzleAttempts(nextGameNr);
      setGuestPuzzleAttempts(nextGameNr);
    }

    if (ratingUpdate.points !== undefined) {
      const nextPoints = normalizePuzzleAttempts(ratingUpdate.points);
      setPuzzlePoints(nextPoints);
      if (!username) {
        writeGuestPuzzlePoints(nextPoints);
      }
    }
    if (ratingUpdate.pointsEarned !== undefined) {
      setLastPointsEarned(normalizePuzzleAttempts(ratingUpdate.pointsEarned));
    }
    if (ratingUpdate.streakBonus !== undefined) {
      setLastStreakBonus(normalizePuzzleAttempts(ratingUpdate.streakBonus));
    }
    if (ratingUpdate.cleanSolveStreak !== undefined) {
      setCleanSolveStreak(
        normalizePuzzleAttempts(ratingUpdate.cleanSolveStreak),
      );
    }
    if (ratingUpdate.rank !== undefined) {
      setRank(ratingUpdate.rank);
    }
    if (ratingUpdate.nextRank !== undefined) {
      setNextRank(ratingUpdate.nextRank);
    }
    if (ratingUpdate.nextRankPoints !== undefined) {
      setNextRankPoints(ratingUpdate.nextRankPoints);
    }

    setRatingGameNr(nextGameNr);
    setRatingLeagueName(nextLeagueName);
    if (ratingUpdate.solvedCount !== undefined) {
      setSolvedProgress((currentProgress) => ({
        ...currentProgress,
        solved: Math.max(currentProgress.solved, nextSolvedCount),
      }));
    }

    const nextRatingRevealed = isPuzzleRatingRevealed({
      solved: nextSolvedCount,
    });

    if (!ratingRevealed && nextRatingRevealed) {
      setUnlockedLeague(
        getPuzzleLeagueTier({
          rating: nextRating,
          leagueName: nextLeagueName,
        }),
      );
    }
  };

  const guestRatingKFactor =
    solvedProgress.solved < PUZZLE_PLACEMENT_SOLVES
      ? PUZZLE_PLACEMENT_K_FACTOR
      : PUZZLE_STANDARD_K_FACTOR;

  return (
    <>
      <Head>
        <title>Chess Hotel | Puzzle Challenge</title>
        <meta
          name="description"
          content="Solve rated chess puzzles, earn puzzle medals, and improve your tactical vision."
        />
      </Head>
      <main className="min-h-[calc(100vh-80px)] px-2 pb-20 md:px-4">
        {currentPuzzle ? (
          <PuzzleBoard
            key={currentPuzzle.puzzleId || currentPuzzle.fen}
            fen={currentPuzzle.fen}
            moves={currentPuzzle.moves}
            onNext={handleNext}
            onComplete={handleComplete}
            onResult={handleResult}
            onRatingChange={handleRatingChange}
            puzzleRating={currentPuzzle.rating}
            userElo={puzzleRating}
            gameNr={ratingGameNr}
            leagueName={ratingLeagueName}
            solvedProgress={solvedProgress}
            puzzlePoints={puzzlePoints}
            lastPointsEarned={lastPointsEarned}
            lastStreakBonus={lastStreakBonus}
            rank={rank}
            nextRank={nextRank}
            nextRankPoints={nextRankPoints}
            cleanSolveStreak={cleanSolveStreak}
            nextLoading={nextLoading}
            puzzleDifficulty={puzzleDifficulty}
            ratingKFactor={guestRatingKFactor}
            ratingRevealed={ratingRevealed}
            onPuzzleDifficultyChange={handleDifficultyChange}
          />
        ) : (
          <PuzzleChallengeMessage
            title="No puzzle found"
            text="Try again in a moment."
          />
        )}
      </main>
      {unlockedLeague && (
        <PuzzleLeagueUnlockedPopup
          tier={unlockedLeague}
          onClose={() => setUnlockedLeague(null)}
        />
      )}
    </>
  );
}
